import { useState } from "react";
import { ConnectionFormDataController } from "../formController";
import { Version } from "../../types/version";
import { Person } from "../../types/person";
import {
  ReferenceVersionConnection,
  SystemVersionConnectionDirection,
  SystemVersionReferenceToEntity,
} from "../../types/referenceVersion";
import useReferenceVersionQuery from "../../hooks/query/connections/useReferenceVersionQuery";
import { CreateEdgeData } from "../../hooks/query/connections/connectionQueryClient";
import useReferenceVersionLogic from "../../hooks/logic/connections/referenceVersionLogic";

interface ReferenceVersionController {
  from?: Version | null;
  to?: Version | Person | null;
  referenceToEntity: SystemVersionReferenceToEntity;
  connectionDirection: SystemVersionConnectionDirection;
}

export const useReferenceVersionConnectionController = ({
  from = null,
  to = null,
  referenceToEntity,
  connectionDirection,
}: ReferenceVersionController): ConnectionFormDataController<
  ReferenceVersionConnection,
  Version,
  Person | Version
> => {
  if ((from === null && to === null) || (from !== null && to !== null)) {
    throw new Error("Exactly one of 'from' or 'to' must be specified");
  }

  const [id, setId] = useState<string | null>(null);
  const direction: "inbound" | "outbound" =
    from === null ? "inbound" : "outbound";
  const anchorEntity: Version | Person = (from || to)!;
  const { useFetchById, useCreate, useUpdate } = useReferenceVersionQuery(
    referenceToEntity,
    connectionDirection
  );
  const { defaultReferenceVersion, metadataFieldNames, getDisplayName } =
    useReferenceVersionLogic(from, to, referenceToEntity, connectionDirection);
  const fetchById = useFetchById(id || "");
  const create = useCreate();
  const update = useUpdate();
  const [isDialogShown, setShowDialog] = useState(false);

  const userEnums = ["system_reference_to_entity", "version_reference"];

  const openCreateDialog = () => {
    setId(null);
    setShowDialog(true);
  };

  const openEditDialog = (id: string) => {
    setId(id);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setId(null);
  };

  const fetchData = async (): Promise<ReferenceVersionConnection> => {
    if (id === null) {
      return defaultReferenceVersion;
    }
    const res = await fetchById.refetch();
    const connectionData = res.data;
    if (!connectionData || res.isError) {
      throw new Error("Reference version connection not found");
    }
    return connectionData;
  };

  const onSubmit = async (
    data: Partial<ReferenceVersionConnection>
  ): Promise<ReferenceVersionConnection> => {
    const metadata: Partial<Omit<ReferenceVersionConnection, "_from" | "_to">> =
      {
        version_reference_type: data.version_reference_type,
        notes: data.notes || "",
        internal_notes: data.internal_notes || "",
        attributes_metadata: {
          ...defaultReferenceVersion.attributes_metadata,
          ...data.attributes_metadata,
        },
      };

    if (isCreateMode) {
      metadata.system_reference_to_entity = referenceToEntity;
      metadata.system_referring_to_version_connection_type = connectionDirection;
    }
    const updateData: CreateEdgeData<ReferenceVersionConnection> = {
      _from: data.fromDoc?._id || from?._id || "",
      _to: data.toDoc?._id || to?._id || "",
      metadata,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...metadata },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Reference Version Connection");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Reference Version Connection");
      }
    }

    setId(null);
    return result;
  };

  const isCreateMode = id === null;

  const title = (data: ReferenceVersionConnection) =>
    `${isCreateMode ? "Edit" : "Add"} ${getDisplayName(data)}`;

  return {
    defaultValues: defaultReferenceVersion,
    metadataFieldNames,
    isCreateMode,
    userEnums,
    title,
    fetchData,
    onSubmit,
    formCallback: closeDialog,
    openEditDialog,
    openCreateDialog,
    closeDialog,
    isDialogShown,
    direction,
    anchorEntity,
    from: from || null,
    to: to || null,
  };
};
